import { ChangeEvent, FC, useEffect, useState } from 'react';
import Header from '../../../common/Header/Header';
import { H2 } from '../../../common/Headings/Headings.styles';
import Button from '../../../components/Button/Button';
import Dropdown from '../../../components/Dropdown/Dropdown';
import Table from '../../../components/Table/Table';
import { showToast } from '../../../components/Toast/Toast';
import { ToastTypes } from '../../../components/Toast/Toast.types';
import { RawProdUomKeys, RAW_PROD_UOM } from '../../../constants/RawProds';
import { useMenus } from '../../../services/menu/menu.service';
import { Menu } from '../../../services/menu/menu.types';
import { useRawProdsInMenu } from '../../../services/rawProd/rawProd.service';
import { useFetchErrorToast } from '../../../utils/useFetchErrorToasts/useFetchErrorToast';
import { ComparePage, DropdownButtonWrapper, DropdownWrapper } from '../CompareMenus/CompareMenus.styles';

export const CompareInventory: FC = () => {
  const [menuOneNo, setMenuOneNo] = useState<number | undefined>(undefined);
  const [menuTwoNo, setMenuTwoNo] = useState<number | undefined>(undefined);
  const [menuOneNoError, setMenuOneNoError] = useState('');
  const [menuTwoNoError, setMenuTwoNoError] = useState('');
  const [menuOneItemRecipes, setMenuOneItemRecipes] = useState<any>([]);
  const [menuTwoItemRecipes, setMenuTwoItemRecipes] = useState<any>([]);
  const [searchActive, setSearchActive] = useState<boolean>(false);

  const { data: menus } = useMenus({}, true, 20000);

  const {
    data: menuOneRawProductsData,
    isError: menuOneRawProductsError,
    remove: removeMenuOneRawProducts,
  } = useRawProdsInMenu({ menuNo: menuOneNo?.toString() || '', mxRef: 'NATION' }, searchActive);

  const {
    data: menuTwoRawProductsData,
    isError: menuTwoRawProductsError,
    remove: removeMenuTwoRawProducts,
  } = useRawProdsInMenu({ menuNo: menuTwoNo?.toString() || '', mxRef: 'NATION' }, searchActive);

  useFetchErrorToast([
    {
      isError: menuOneRawProductsError,
      label: 'Error fetching raw products in menu one',
      id: 'menuOneRawProductsFetchError',
    },
    {
      isError: menuTwoRawProductsError,
      label: 'Error fetching raw products in menu two',
      id: 'menuTwoRawProductsFetchError',
    },
  ]);

  useEffect(() => {
    if (menuOneRawProductsData && menuTwoRawProductsData) {
      setMenuOneItemRecipes(menuOneRawProductsData?.data);
      setMenuTwoItemRecipes(menuTwoRawProductsData?.data);
      setSearchActive(false);
      removeMenuOneRawProducts();
      removeMenuTwoRawProducts();
      setMenuOneNoError('');
      setMenuTwoNoError('');
    }
  }, [menuOneRawProductsData, menuTwoRawProductsData]);

  const menuOptions =
    menus?.data?.map((item: Menu) => {
      return {
        value: item?.menuNo?.toString(),
        label: `${item.storeNo} ~ ${item.menuNo} ~ ${item.description} ~ ${item.statusNo}`,
      };
    }) || [];

  const handleMenuOneSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    if (menuOneNoError) setMenuOneNoError('');
    setMenuOneNo(Number(event.target.value));
    setSearchActive(false);
  };

  const handleMenuTwoSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    if (menuTwoNoError) setMenuTwoNoError('');
    setMenuTwoNo(Number(event.target.value));
    setSearchActive(false);
  };

  const handleCompareClick = () => {
    setSearchActive(true);
    if (menuOneNo === undefined) {
      setSearchActive(false);
      setMenuOneNoError('Menu required');
    }
    if (menuTwoNo === undefined) {
      setSearchActive(false);
      setMenuTwoNoError('Menu required');
    }
    if (menuOneNo !== undefined && menuTwoNo !== undefined && menuOneNo === menuTwoNo) {
      setSearchActive(false);
      showToast({
        label: 'Please select two different menus to compare.',
        id: 'compareInventoryMatchError',
        toastType: ToastTypes.Error,
      });
    }
  };

  return (
    <>
      <Header breadcrumb={{ label: 'Back To All Menus', to: '/menus' }} title='Compare Menus' />
      <ComparePage>
        <H2>Menus to Compare</H2>
        <DropdownButtonWrapper>
          <DropdownWrapper>
            <Dropdown
              label='Menu 1'
              id='menuOneNo'
              options={menuOptions}
              value={menuOneNo?.toString()}
              onChange={handleMenuOneSelect}
              errorText={menuOneNoError}
            />
            <Dropdown
              label='Menu 2'
              id='menuTwoNo'
              options={menuOptions}
              value={menuTwoNo?.toString()}
              onChange={handleMenuTwoSelect}
              errorText={menuTwoNoError}
            />
            <Button label='Compare' onClick={handleCompareClick} />
          </DropdownWrapper>
          {/* Out of scope */}
          {/* <Button label='Print' variant={Variant.Secondary} onClick={() => {}} /> */}
        </DropdownButtonWrapper>
        <Table
          showHeader={true}
          headerTitle='Menu 1 Results View'
          rowData={menuOneItemRecipes}
          columnDefs={[
            { minWidth: 130, field: 'printSeq', headerName: 'Print Sequence', floatingFilter: false, flex: 1 },
            { minWidth: 130, field: 'description', headerName: 'Description', floatingFilter: false, flex: 1 },
            { minWidth: 130, field: 'moaRawProdNo', headerName: 'MOA Raw Product', floatingFilter: false, flex: 1 },
            { minWidth: 110, field: 'rawProdNo', headerName: 'Raw Product', floatingFilter: false, flex: 1 },
            { minWidth: 110, field: 'rawProdTpCode', headerName: 'Type', floatingFilter: false, flex: 1 },
            { minWidth: 110, field: 'pcaNo', headerName: 'Sub Yield', floatingFilter: false, flex: 1 },
            { minWidth: 110, field: 'classDesc', headerName: 'Yield', floatingFilter: false, flex: 1 },
            {
              minWidth: 130,
              field: 'conversionFactor',
              headerName: 'Conversion Factor',
              floatingFilter: false,
              flex: 1,
            },
            { minWidth: 110, field: 'occasionDesc', headerName: 'Occasion', floatingFilter: false, flex: 1 },
            {
              minWidth: 130,
              field: 'invenUomNo',
              headerName: 'Unit of Measure',
              floatingFilter: false,
              flex: 1,
              valueFormatter: ({ value }) => RAW_PROD_UOM[value as RawProdUomKeys] || '',
            },
            {
              minWidth: 130,
              field: 'accountCode',
              headerName: 'Account Number',
              floatingFilter: false,
              flex: 1,
              resizable: false,
            },
          ]}
          returnedRecords={menuOneItemRecipes?.length || 0}
          height={'600px'}
          width={'100%'}
        />
        <Table
          showHeader={true}
          headerTitle='Menu 2 Results View'
          rowData={menuTwoItemRecipes}
          columnDefs={[
            { minWidth: 140, field: 'printSeq', headerName: 'Print Sequence', floatingFilter: false, flex: 1 },
            { minWidth: 140, field: 'description', headerName: 'Description', floatingFilter: false, flex: 1 },
            { minWidth: 140, field: 'moaRawProdNo', headerName: 'MOA Raw Product', floatingFilter: false, flex: 1 },
            { minWidth: 110, field: 'rawProdNo', headerName: 'Raw Product', floatingFilter: false, flex: 1 },
            { minWidth: 110, field: 'rawProdTpCode', headerName: 'Type', floatingFilter: false, flex: 1 },
            { minWidth: 110, field: 'pcaNo', headerName: 'Sub Yield', floatingFilter: false, flex: 1 },
            { minWidth: 110, field: 'classDesc', headerName: 'Yield', floatingFilter: false, flex: 1 },
            {
              minWidth: 140,
              field: 'conversionFactor',
              headerName: 'Conversion Factor',
              floatingFilter: false,
              flex: 1,
            },
            { minWidth: 110, field: 'occasionDesc', headerName: 'Occasion', floatingFilter: false, flex: 1 },
            {
              minWidth: 140,
              field: 'invenUomNo',
              headerName: 'Unit of Measure',
              floatingFilter: false,
              flex: 1,
              valueFormatter: ({ value }) => RAW_PROD_UOM[value as RawProdUomKeys] || '',
            },
            {
              minWidth: 140,
              field: 'accountCode',
              headerName: 'Account Number',
              floatingFilter: false,
              flex: 1,
              resizable: false,
            },
          ]}
          returnedRecords={menuTwoItemRecipes?.length || 0}
          height={'600px'}
          width={'100%'}
        />
      </ComparePage>
    </>
  );
};

export default CompareInventory;
